import { lazy, Suspense, useEffect, useRef, useState } from "react";
import type { SynthesisDraft } from "../domain/ai/contracts";
import type { ReportRequest } from "../domain/report/contracts";
import type { AuthClientLike } from "../features/auth/AuthGate";
import { buildVietnameseReportProjection } from "../domain/presentation";
import { buildNarrative } from "../domain/narrative/build-narrative";
import type * as buildReportModule from "../features/report/build-report";
import { hashReportInput } from "../domain/report/hash";
import { IntakeForm } from "../features/intake/IntakeForm";
import {
  getNarrativeDraft,
  listSavedInputs,
  saveNarrativeDraft,
  saveReportInput,
  type NarrativeStoreClient,
  type SupabaseClientLike,
} from "../features/report/persistence";
import type { SessionReport } from "../features/report/session";
import {
  NARRATIVE_PROMPT_VERSION,
  requestNarrativeSynthesis,
} from "../features/report/narrative-synthesis";
import type { AiNarrativeState } from "../features/report/ReportView";
import { createSupabaseClient } from "../lib/supabase";

type AppClient = SupabaseClientLike & AuthClientLike & NarrativeStoreClient;

const AuthGate = lazy(() =>
  import("../features/auth/AuthGate").then((m) => ({ default: m.AuthGate })),
);
const AccountPanel = lazy(() =>
  import("../features/auth/AccountPanel").then((m) => ({
    default: m.AccountPanel,
  })),
);
const HistoryView = lazy(() =>
  import("../features/history/HistoryView").then((m) => ({
    default: m.HistoryView,
  })),
);
const ReportView = lazy(() =>
  import("../features/report/ReportView").then((m) => ({
    default: m.ReportView,
  })),
);

type AppView = "intake" | "report" | "history" | "auth";

const methods = [
  {
    title: "Bát Tự Solar-First",
    body: "Tứ trụ từ ngày dương và giờ sinh chính xác. Lịch âm và giờ gần đúng bị chặn.",
    badge: "Độ chính xác cao",
  },
  {
    title: "Tử Vi 12 Cung",
    body: "Bản lưu kỹ thuật 12 cung và tên sao. Không diễn giải hay khuyến nghị.",
    badge: "Dữ liệu tuyệt đối",
  },
  {
    title: "Kinh Dịch",
    body: "Gieo quẻ theo thời điểm câu hỏi. Chỉ số quẻ và hào động, không đặt tên suy diễn.",
    badge: "Dự báo động",
  },
  {
    title: "Pythagoras",
    body: "Sáu chỉ số từ tên và ngày sinh. Quy ước Y là phụ âm.",
    badge: "Ma trận số liệu",
  },
] as const;

function LoadingScreen() {
  return (
    <main className="page-canvas">
      <p>Đang tải…</p>
    </main>
  );
}

type ReportBuilder = typeof buildReportModule;
let reportBuilder: Promise<ReportBuilder> | null = null;

function loadReportBuilder(): Promise<ReportBuilder> {
  reportBuilder ??= import("../features/report/build-report");
  return reportBuilder;
}

function splitParagraphs(bodyVi: string): readonly string[] {
  const parts = bodyVi
    .split(/\n{2,}/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  return parts.length > 0 ? parts : [bodyVi];
}

function isSynthesisDraft(value: unknown): value is SynthesisDraft {
  if (typeof value !== "object" || value === null || !("sections" in value)) {
    return false;
  }
  const sections: unknown = value.sections;
  return (
    Array.isArray(sections) &&
    sections.every(
      (section) =>
        typeof section === "object" &&
        section !== null &&
        "id" in section &&
        "titleVi" in section &&
        "bodyVi" in section &&
        "citationIds" in section,
    )
  );
}

// Cloud writes (intake + AI draft) used to fail silently into console.warn,
// which hid a two-day outage where nothing persisted. Surface every failure
// to the user, and name the auth-shaped ones so they know a re-login fixes it.
function describePersistFailure(error: unknown, what: string): string {
  const message = error instanceof Error ? error.message : String(error);
  const authShaped =
    /\b401\b|jwt|unauthorized|permission denied|row-level security|not authenticated|invalid (token|claim|jwt)/i.test(
      message,
    );
  if (authShaped) {
    return `Phiên đăng nhập đã hết hạn — ${what} CHƯA được lưu. Hãy đăng xuất, đăng nhập lại rồi thử lại.`;
  }
  return `Chưa lưu được ${what} lên máy chủ. Kết quả vẫn hiển thị nhưng có thể mất khi tải lại trang.`;
}

function toDoneNarrative(
  draft: SynthesisDraft,
  fromCache: boolean,
): AiNarrativeState {
  return {
    status: "done",
    fromCache,
    sections: draft.sections.map((section) => ({
      id: section.id,
      titleVi: section.titleVi,
      paragraphsVi: splitParagraphs(section.bodyVi),
      citationIds: section.citationIds,
    })),
  };
}

export function App() {
  const [view, setView] = useState<AppView>("intake");
  const [reports, setReports] = useState<SessionReport[]>([]);
  const [unlocked, setUnlocked] = useState(false);
  const [aiNarrativeByReportId, setAiNarrativeByReportId] = useState<
    Record<string, AiNarrativeState>
  >({});
  // Non-blocking banner shown when a cloud write (intake or AI draft) fails.
  const [persistNotice, setPersistNotice] = useState<string | null>(null);
  const cloudLoadsRef = useRef(0);
  const [supabase] = useState<AppClient | null>(() => {
    try {
      // The real client's query-builder generics are too deep for TS to
      // structurally match against the narrow *Like interfaces above
      // (TS2589); it does satisfy them at runtime, so narrow explicitly.
      return createSupabaseClient() as unknown as AppClient;
    } catch {
      return null;
    }
  });

  async function loadCloudReports() {
    if (!supabase) {
      return;
    }
    try {
      const saved = await listSavedInputs(supabase);
      const existing = new Set(reports.map((entry) => entry.inputHash));
      const additions: SessionReport[] = [];
      for (const entry of saved) {
        const hash = await hashReportInput(entry.request);
        if (existing.has(hash)) {
          continue;
        }
        existing.add(hash);
        const { buildSessionReport } = await loadReportBuilder();
        additions.push(
          await buildSessionReport(
            entry.request,
            reports.length + additions.length + 1,
          ),
        );
      }
      setReports((current) => [...current, ...additions]);
    } catch (error) {
      console.warn("[history] Không tải được kho lưu trữ:", error);
    }
  }

  function unlockAndLoad() {
    setUnlocked(true);
    cloudLoadsRef.current += 1;
    if (cloudLoadsRef.current > 1) {
      return;
    }
    void loadCloudReports();
  }

  // A magic-link click lands the user on any view (default intake), not the
  // auth view, so AuthGate may never mount. Restore an existing session here;
  // supabase-js has already consumed the link's #access_token fragment during
  // client initialization, which getSession() awaits.
  useEffect(() => {
    if (!supabase) {
      return;
    }
    void supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        unlockAndLoad();
      }
    });
    // supabase is created once in useState.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const latest = reports.at(-1);
  // Locked users never reach intake/report/history: every view resolves to
  // the auth gate until a session exists.
  const activeView: AppView = !unlocked && view !== "auth" ? "auth" : view;

  // Hydrates one report's saved AI draft on demand (viewing it, not on every
  // bulk history load) so a page reload never forces a fresh Gemini call for
  // a report that was already synthesized.
  //
  // Race guard: the button that triggers a manual synthesizeAiNarrative is
  // only hidden once this key's state stops being empty/idle, but this
  // function is scheduled a microtask after the effect runs (see the
  // deferred call below) — so a click can land in that gap. React state
  // updaters run asynchronously (never synchronously right after the
  // setState call), so control flow here can't branch on "did I win the
  // claim" — instead BOTH writes are individually guarded to be no-ops if
  // something else already owns the key, and the fetch always runs (its
  // result is simply discarded when the guard fails; the read is cheap,
  // unlike the Gemini call synthesizeAiNarrative makes).
  async function hydrateAiNarrative(report: SessionReport) {
    if (!supabase) {
      return;
    }
    const key = `${report.id}-v${report.version}`;
    setAiNarrativeByReportId((current) =>
      current[key] ? current : { ...current, [key]: { status: "hydrating" } },
    );
    let nextState: AiNarrativeState = { status: "idle" };
    try {
      const draft = await getNarrativeDraft(supabase, report.inputHash);
      if (isSynthesisDraft(draft)) {
        nextState = toDoneNarrative(draft, true);
      }
    } catch (error) {
      console.warn("[history] Không tải được luận giải AI đã lưu:", error);
    }
    setAiNarrativeByReportId((current) =>
      current[key]?.status === "hydrating"
        ? { ...current, [key]: nextState }
        : current,
    );
  }

  // Whenever the visible report changes, look up its saved AI draft once
  // instead of forcing the user to press "Xem luận giải tổng hợp" again.
  useEffect(() => {
    if (!latest || !supabase) {
      return;
    }
    const key = `${latest.id}-v${latest.version}`;
    if (aiNarrativeByReportId[key]) {
      return;
    }
    // Deferred like the magic-link restore above: the effect body itself
    // stays synchronous and side-effect-free, the state update happens in
    // the continuation.
    void Promise.resolve().then(() => hydrateAiNarrative(latest));
    // Only re-run when the visible report identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latest?.id, latest?.version, supabase]);

  async function handleValid(request: ReportRequest) {
    const { buildSessionReport } = await loadReportBuilder();
    const report = await buildSessionReport(request, reports.length + 1);
    setReports((current) => [...current, report]);
    setView("report");
    if (supabase) {
      try {
        await saveReportInput(supabase, request);
        setPersistNotice(null);
      } catch (error) {
        console.warn("[history] Không lưu được bản nhập:", error);
        setPersistNotice(describePersistFailure(error, "bản nhập báo cáo"));
      }
    }
  }

  async function synthesizeAiNarrative(report: SessionReport) {
    if (!supabase) {
      return;
    }
    const key = `${report.id}-v${report.version}`;
    setAiNarrativeByReportId((current) => ({
      ...current,
      [key]: { status: "loading" },
    }));
    const result = await requestNarrativeSynthesis(supabase, report);
    let nextState: AiNarrativeState;
    if (result.kind === "completed") {
      // Persist the non-deterministic draft so reloads and history reuse it
      // instead of paying for a new multi-call synthesis.
      void saveNarrativeDraft(
        supabase,
        report.inputHash,
        NARRATIVE_PROMPT_VERSION,
        result.draft,
      ).then(
        () => setPersistNotice(null),
        (error: unknown) => {
          console.warn("[history] Không lưu được luận giải AI:", error);
          setPersistNotice(
            describePersistFailure(error, "kết quả luận giải AI"),
          );
        },
      );
      nextState = toDoneNarrative(result.draft, false);
    } else if (result.kind === "blocked") {
      nextState = { status: "blocked", messageVi: result.messageVi };
    } else {
      nextState = { status: "error", messageVi: result.message };
    }
    setAiNarrativeByReportId((current) => ({
      ...current,
      [key]: nextState,
    }));
  }

  async function rerunLatest() {
    if (!latest) {
      return;
    }
    const { buildSessionReport } = await loadReportBuilder();
    const report = await buildSessionReport(latest.request, latest.version + 1);
    setReports((current) => [...current, report]);
    setView("report");
  }

  return (
    <div className="app-shell">
      <header className="site-header">
        <button
          type="button"
          className="brand"
          onClick={() => setView("intake")}
        >
          <img src="/logo.png" alt="" width={40} height={40} />
          <span>Khí Vận Tổng Hợp</span>
        </button>
        <nav className="site-nav" aria-label="Chính">
          <button
            type="button"
            aria-current={activeView === "intake" ? "page" : undefined}
            onClick={() => setView("intake")}
          >
            Thiết lập
          </button>
          <button
            type="button"
            aria-current={activeView === "report" ? "page" : undefined}
            onClick={() => latest && setView("report")}
            disabled={!latest}
          >
            Báo cáo
          </button>
          <button
            type="button"
            aria-current={activeView === "history" ? "page" : undefined}
            onClick={() => setView("history")}
          >
            Kho lưu trữ
          </button>
          <button
            type="button"
            aria-current={activeView === "auth" ? "page" : undefined}
            onClick={() => setView("auth")}
          >
            Nhập Mã Mời
          </button>
        </nav>
        <span className="beta-chip">Bản Thử Nghiệm Kín v0.1.0</span>
      </header>

      {persistNotice ? (
        <div className="persist-notice" role="alert">
          <span>{persistNotice}</span>
          <button
            type="button"
            className="persist-notice-dismiss"
            aria-label="Đóng thông báo"
            onClick={() => setPersistNotice(null)}
          >
            ×
          </button>
        </div>
      ) : null}

      {activeView === "auth" ? (
        <Suspense fallback={<LoadingScreen />}>
          {!supabase ? (
            <main className="page-canvas">
              <p role="alert">Thiếu cấu hình Supabase.</p>
            </main>
          ) : unlocked ? (
            <AccountPanel
              authClient={supabase}
              onSignedOut={() => {
                setUnlocked(false);
                cloudLoadsRef.current = 0;
                setReports([]);
                setAiNarrativeByReportId({});
                setPersistNotice(null);
                setView("auth");
              }}
            />
          ) : (
            <AuthGate
              authClient={supabase}
              onUnlocked={() => {
                setView("intake");
                unlockAndLoad();
              }}
            />
          )}
        </Suspense>
      ) : null}

      {activeView === "intake" ? (
        <main className="page-canvas landing">
          <section className="hero" aria-labelledby="page-title">
            <img
              className="hero-logo"
              src="/logo.png"
              alt="Khí Vận Tổng Hợp"
              width={192}
              height={192}
            />
            <p className="eyebrow">Beta kín · Báo cáo có căn cứ</p>
            <h1 id="page-title">Khí Vận Tổng Hợp · Bách Gia Quy Nhất</h1>
            <p className="summary">
              Sự hội tụ của các phương pháp luận giải mệnh lý, xây dựng trên nền
              tảng toán học tất định. AI chỉ diễn đạt facts đã tính.
            </p>
            <p className="safety-note">
              Nội dung mang tính tham khảo, không thay thế tư vấn y tế, pháp lý
              hoặc tài chính.
            </p>
          </section>
          <div className="divider-knot" aria-hidden="true">
            <span>◆</span>
          </div>
          <section className="method-grid" aria-label="Phương pháp sẵn sàng">
            {methods.map((method) => (
              <article key={method.title} className="lacquer-card method-card">
                <h3>{method.title}</h3>
                <p>{method.body}</p>
                <span className="jade-badge">{method.badge}</span>
              </article>
            ))}
          </section>
          <IntakeForm onValidSubmit={handleValid} />
        </main>
      ) : null}

      {activeView === "report" && latest
        ? (() => {
            const projection = buildVietnameseReportProjection(latest);
            const aiKey = `${latest.id}-v${latest.version}`;
            return (
              <Suspense fallback={<LoadingScreen />}>
                <ReportView
                  projection={projection}
                  narrativeSections={buildNarrative(projection)}
                  aiNarrative={aiNarrativeByReportId[aiKey]}
                  onRequestAiNarrative={
                    supabase
                      ? () => void synthesizeAiNarrative(latest)
                      : undefined
                  }
                  onRegenerateAiNarrative={
                    supabase
                      ? () => void synthesizeAiNarrative(latest)
                      : undefined
                  }
                  onRerun={rerunLatest}
                  onOpenHistory={() => setView("history")}
                />
              </Suspense>
            );
          })()
        : null}

      {activeView === "history" ? (
        <Suspense fallback={<LoadingScreen />}>
          <HistoryView
            reports={reports}
            onClear={() => {
              setReports([]);
              setView("intake");
            }}
            onCreateNew={() => setView("intake")}
          />
        </Suspense>
      ) : null}

      <footer className="site-footer">
        <strong>Khí Vận Tổng Hợp</strong>
        <p>
          Nội dung mang tính tham khảo, không thay thế tư vấn y tế, pháp lý hoặc
          tài chính.
        </p>
      </footer>
    </div>
  );
}
